// Helpers para los multi-selects de Plan y Plataforma del form de cliente (coexistencia).
//
// Modelo: el usuario guarda `plan_id` (plan PRIMARIO regular), `platform` (canal regular:
// otrix_app/otrix_gym) y `can_active_plus` (capacidad Active Plus, aditiva). Los planes ADICIONALES
// (pilates/nutrición/especializadas) viven en los pagos (`active_plans`) y se muestran solo-lectura.

const num = (value) => Number(value);

const planOption = (planId, planOptions) =>
  planOptions.find((option) => num(option.value) === num(planId));

const isActivePlusPlan = (planId, planOptions) =>
  planOption(planId, planOptions)?.platform === 'active_plus';

const isPrimaryRegularPlan = (planId, planOptions) => {
  const option = planOption(planId, planOptions);
  return Boolean(option && option.type === 'primary' && option.platform !== 'active_plus');
};

const firstActivePlusPlanId = (planOptions) =>
  planOptions.find((option) => option.platform === 'active_plus')?.value ?? null;

/**
 * Opciones del dropdown de PLAN: planes primarios regulares (editables) + Active Plus (editable, chip)
 * + adicionales que el cliente YA tiene (solo-lectura, chip "Adicional").
 */
export const buildPlanFieldOptions = (planOptions = [], activePlans = [], chips = {}) => {
  const heldIds = new Set((activePlans || []).map((plan) => num(plan.id)));

  return planOptions
    .filter((option) => option.platform === 'active_plus' || option.type === 'primary' || heldIds.has(num(option.value)))
    .map((option) => {
      if (option.platform === 'active_plus') return { ...option, chip: chips.activePlus };
      if (option.type !== 'primary') return { ...option, chip: chips.additional, disabled: true };
      return option;
    });
};

/** Valores iniciales de los multi-selects (platforms/plans) desde los datos del cliente. */
export const buildPlanPlatformInitial = (client, planOptions = []) => {
  const canActivePlus = Boolean(client?.can_active_plus);
  const primaryId =
    client?.plan_id?.id && isPrimaryRegularPlan(client.plan_id.id, planOptions) ? num(client.plan_id.id) : null;
  // Solo se muestra el canal regular si el cliente tiene un plan primario (un Active Plus puro no lo tiene).
  const channel = client?.platform && client.platform !== 'active_plus' ? client.platform : 'otrix_app';
  const activePlusPlanId = firstActivePlusPlanId(planOptions);
  const additionalIds = (client?.active_plans || [])
    .filter((plan) => plan.type !== 'primary' && plan.platform !== 'active_plus')
    .map((plan) => num(plan.id));

  const platforms = [...(primaryId ? [channel] : []), ...(canActivePlus ? ['active_plus'] : [])];
  const plans = [
    ...(primaryId ? [primaryId] : []),
    ...(canActivePlus && activePlusPlanId != null ? [activePlusPlanId] : []),
    ...additionalIds
  ];

  return { platforms, plans };
};

/** Al enviar: arrays de los multi-selects → escalares que acepta el backend (platform/plan_id/can_active_plus). */
export const derivePlanPlatformInput = (platforms = [], plans = [], planOptions = []) => {
  const canActivePlus =
    (platforms || []).includes('active_plus') || (plans || []).some((id) => isActivePlusPlan(id, planOptions));
  const platform = (platforms || []).find((value) => value !== 'active_plus') || 'otrix_app';
  const primary = (plans || []).find((id) => isPrimaryRegularPlan(id, planOptions));

  return {
    platform,
    plan_id: primary != null ? num(primary) : null,
    can_active_plus: canActivePlus
  };
};
